// Admin Social — configure ContentStudio integration & test dispatch.
// Maps each source (Academy / Maps / Insights) to:
//   - a Content Category (CS queue)
//   - 5 accounts: FB EN, FB FR, IG EN, IG FR, LinkedIn
//   - hashtags EN + FR (appended as first_comment on the post)
// Includes a Preview & Dispatch panel to test the pipeline end-to-end.
import { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Send, Eye, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import TemplateEditor from "@/components/admin/TemplateEditor";

const SOURCES = [
  { key: "academy", label: "geoVINUM Academy" },
  { key: "maps", label: "geoVINUM Maps" },
  { key: "insights", label: "Insights (blog)" },
];

const platformLabel = (p) => ({ facebook: "Facebook", instagram: "Instagram", linkedin: "LinkedIn", pinterest: "Pinterest" }[p] || p);

function AccountPicker({ accounts, platform, value, onChange, testId }) {
  const filtered = accounts.filter((a) => a.platform === platform);
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm bg-white"
      data-testid={testId}
    >
      <option value="">— Aucun {platformLabel(platform)} —</option>
      {filtered.map((a) => (
        <option key={a._id} value={a._id}>{a.account_name || a._id}</option>
      ))}
    </select>
  );
}

const AdminSocialPage = () => {
  const [health, setHealth] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dispatch panel state
  const [dispSource, setDispSource] = useState("insights");
  const [dispTitleFr, setDispTitleFr] = useState("");
  const [dispTitleEn, setDispTitleEn] = useState("");
  const [dispExcerptFr, setDispExcerptFr] = useState("");
  const [dispExcerptEn, setDispExcerptEn] = useState("");
  const [dispUrl, setDispUrl] = useState("");
  const [dispCover, setDispCover] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [h, ws, cfg] = await Promise.all([
        adminApi.socialHealth().catch((e) => ({ ok: false, error: e.message })),
        adminApi.socialWorkspaces().catch(() => ({ data: [] })),
        adminApi.socialGetConfig(),
      ]);
      setHealth(h);
      setWorkspaces(ws.data || []);
      setConfig(cfg);
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable */
  useEffect(() => { refresh(); }, []);

  // Whenever workspace changes, fetch accounts + categories
  const wsId = config?.workspace_id;
  useEffect(() => {
    if (!wsId) { setAccounts([]); setCategories([]); return; }
    (async () => {
      const [acc, cat] = await Promise.all([
        adminApi.socialAccounts(wsId).catch(() => ({ data: [] })),
        adminApi.socialCategories(wsId).catch(() => ({ data: [] })),
      ]);
      setAccounts(acc.data || []);
      setCategories(cat.data || []);
    })();
  }, [wsId]);
  /* eslint-enable */

  const setCfg = (path, val) => {
    setConfig((c) => {
      const next = JSON.parse(JSON.stringify(c || {}));
      const parts = path.split(".");
      let node = next;
      for (let i = 0; i < parts.length - 1; i++) {
        node[parts[i]] = node[parts[i]] || {};
        node = node[parts[i]];
      }
      node[parts[parts.length - 1]] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await adminApi.socialPutConfig(config);
      setConfig(saved);
      setSavedAt(new Date().toISOString());
    } catch (e) { alert(`Erreur sauvegarde : ${e.message}`); }
    finally { setSaving(false); }
  };

  const buildDispatchPayload = () => ({
    source: dispSource,
    title_en: dispTitleEn,
    title_fr: dispTitleFr,
    excerpt_en: dispExcerptEn,
    excerpt_fr: dispExcerptFr,
    url_en: dispUrl,
    url_fr: dispUrl,
    cover_url: dispCover,
  });

  const runPreview = async () => {
    setBusy(true); setPreview(null); setReport(null);
    try { setPreview(await adminApi.socialPreview(buildDispatchPayload())); }
    catch (e) { alert(`Erreur preview : ${e.message}`); }
    finally { setBusy(false); }
  };

  const runDispatch = async () => {
    if (!window.confirm("Envoyer en queue ContentStudio (catégorie configurée) ?")) return;
    setBusy(true); setReport(null);
    try { setReport(await adminApi.socialDispatch(buildDispatchPayload())); }
    catch (e) { alert(`Erreur dispatch : ${e.message}`); }
    finally { setBusy(false); }
  };

  const healthBadge = useMemo(() => {
    if (!health) return null;
    if (health.ok) return <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid="social-health-ok">Connecté · {health.me?.data?.full_name || "OK"}</span>;
    return <span className="text-xs px-2 py-1 rounded-full bg-rose-100 text-rose-700" data-testid="social-health-fail">API Key invalide</span>;
  }, [health]);

  if (loading || !config) {
    return <div className="p-10 text-sm text-[color:var(--gv-ink)]/60" data-testid="admin-social-loading"><Loader2 className="animate-spin inline mr-2" size={14}/> Chargement…</div>;
  }

  return (
    <div data-testid="admin-social" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-1">Social Auto-Publish</h1>
          <p className="text-sm text-[color:var(--gv-ink)]/60">
            Publication automatique sur Facebook · Instagram · LinkedIn via ContentStudio · catégorisée par source
          </p>
        </div>
        <div className="flex items-center gap-3">
          {healthBadge}
          <button onClick={refresh} className="btn-secondary" data-testid="social-refresh"><RefreshCw size={14}/> Refresh</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="social-save">
            {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Sauvegarder
          </button>
        </div>
      </div>

      {/* Workspace + global channels */}
      <section className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid="social-workspace">
        <h2 className="font-display text-lg mb-4">1. Workspace ContentStudio</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-[color:var(--gv-ink)]/60">Workspace</span>
            <select
              value={config.workspace_id || ""}
              onChange={(e) => setCfg("workspace_id", e.target.value)}
              className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm bg-white"
              data-testid="social-workspace-select"
            >
              <option value="">— Choisir un workspace —</option>
              {workspaces.map((w) => (
                <option key={w._id} value={w._id}>{w.name}</option>
              ))}
            </select>
          </label>
          <div className="text-xs text-[color:var(--gv-ink)]/60 flex items-end">
            {accounts.length > 0 ? `${accounts.length} compte${accounts.length>1?"s":""} détecté${accounts.length>1?"s":""} · ${categories.length} catégorie${categories.length>1?"s":""}` : "Sélectionne un workspace pour charger les comptes."}
          </div>
        </div>
      </section>

      {/* Per-language channels */}
      {config.workspace_id && (
        <section className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid="social-channels">
          <h2 className="font-display text-lg mb-1">2. Comptes sociaux par langue</h2>
          <p className="text-xs text-[color:var(--gv-ink)]/60 mb-4">Le même compte LinkedIn peut être réutilisé pour EN et FR.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {["en", "fr"].map((lang) => (
              <div key={lang} className="space-y-3" data-testid={`social-lang-${lang}`}>
                <div className="text-xs uppercase tracking-wide font-semibold text-[color:var(--gv-pink)]">{lang === "en" ? "English" : "Français"}</div>
                {["facebook", "instagram", "linkedin"].map((p) => (
                  <label key={p} className="block">
                    <span className="text-[12px] text-[color:var(--gv-ink)]/70">{platformLabel(p)}</span>
                    <AccountPicker
                      accounts={accounts}
                      platform={p}
                      value={config[lang]?.[p]}
                      onChange={(v) => setCfg(`${lang}.${p}`, v)}
                      testId={`social-acc-${lang}-${p}`}
                    />
                  </label>
                ))}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Sources mapping */}
      {config.workspace_id && (
        <section className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid="social-sources">
          <h2 className="font-display text-lg mb-1">3. Mapping par source de contenu</h2>
          <p className="text-xs text-[color:var(--gv-ink)]/60 mb-4">Chaque source pousse dans une Content Category (queue ContentStudio) avec ses hashtags propres.</p>
          <div className="space-y-5">
            {SOURCES.map((s) => {
              const src = config.sources?.[s.key] || {};
              return (
                <div key={s.key} className="border border-[color:var(--gv-line)] rounded-[4px] p-4 bg-[color:var(--gv-mist)]/30" data-testid={`social-source-${s.key}`}>
                  <div className="text-sm font-semibold text-[color:var(--gv-ink)] mb-3">{s.label}</div>
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    <label className="block">
                      <span className="text-[12px] text-[color:var(--gv-ink)]/70">Template visuel</span>
                      <select
                        value={src.template_variant || "premium"}
                        onChange={(e) => setCfg(`sources.${s.key}.template_variant`, e.target.value)}
                        className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm bg-white"
                        data-testid={`social-template-${s.key}`}
                      >
                        <option value="premium">Premium</option>
                        <option value="punchy">Punchy</option>
                        <option value="minimal">Minimal</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[12px] text-[color:var(--gv-ink)]/70">Content Category — Feed</span>
                      <select
                        value={src.category_id || ""}
                        onChange={(e) => setCfg(`sources.${s.key}.category_id`, e.target.value)}
                        className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm bg-white"
                        data-testid={`social-cat-${s.key}`}
                      >
                        <option value="">— Aucune —</option>
                        {categories.map((c) => (
                          <option key={c._id} value={c._id}>{c.name}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[12px] text-[color:var(--gv-ink)]/70">Content Category — Story (IG/FB)</span>
                      <select
                        value={src.category_story_id || ""}
                        onChange={(e) => setCfg(`sources.${s.key}.category_story_id`, e.target.value)}
                        className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm bg-white"
                        data-testid={`social-cat-story-${s.key}`}
                      >
                        <option value="">— Pas de story —</option>
                        {categories.map((c) => (
                          <option key={c._id} value={c._id}>{c.name}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[12px] text-[color:var(--gv-ink)]/70">Hashtags EN (séparés par espace)</span>
                      <textarea
                        rows={2}
                        value={src.hashtags_en || ""}
                        onChange={(e) => setCfg(`sources.${s.key}.hashtags_en`, e.target.value)}
                        placeholder="#wine #winelovers #geovinum"
                        className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm bg-white"
                        data-testid={`social-tags-en-${s.key}`}
                      />
                    </label>
                    <label className="block">
                      <span className="text-[12px] text-[color:var(--gv-ink)]/70">Hashtags FR</span>
                      <textarea
                        rows={2}
                        value={src.hashtags_fr || ""}
                        onChange={(e) => setCfg(`sources.${s.key}.hashtags_fr`, e.target.value)}
                        placeholder="#vin #amateursdevin #geovinum"
                        className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm bg-white"
                        data-testid={`social-tags-fr-${s.key}`}
                      />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Preview & dispatch */}
      {config.workspace_id && (
        <section className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid="social-dispatch">
          <h2 className="font-display text-lg mb-1">4. Test &amp; dispatch manuel</h2>
          <p className="text-xs text-[color:var(--gv-ink)]/60 mb-4">Génère l&apos;image + les captions et envoie vers la queue ContentStudio.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[12px] text-[color:var(--gv-ink)]/70">Source</span>
              <select value={dispSource} onChange={(e) => setDispSource(e.target.value)} className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm bg-white" data-testid="dispatch-source">
                {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[12px] text-[color:var(--gv-ink)]/70">Cover URL (image source)</span>
              <input value={dispCover} onChange={(e) => setDispCover(e.target.value)} placeholder="https://..." className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm" data-testid="dispatch-cover" />
            </label>
            <label className="block">
              <span className="text-[12px] text-[color:var(--gv-ink)]/70">Title FR</span>
              <input value={dispTitleFr} onChange={(e) => setDispTitleFr(e.target.value)} className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm" data-testid="dispatch-title-fr" />
            </label>
            <label className="block">
              <span className="text-[12px] text-[color:var(--gv-ink)]/70">Title EN</span>
              <input value={dispTitleEn} onChange={(e) => setDispTitleEn(e.target.value)} className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm" data-testid="dispatch-title-en" />
            </label>
            <label className="block">
              <span className="text-[12px] text-[color:var(--gv-ink)]/70">Excerpt FR</span>
              <textarea rows={2} value={dispExcerptFr} onChange={(e) => setDispExcerptFr(e.target.value)} className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm" data-testid="dispatch-excerpt-fr" />
            </label>
            <label className="block">
              <span className="text-[12px] text-[color:var(--gv-ink)]/70">Excerpt EN</span>
              <textarea rows={2} value={dispExcerptEn} onChange={(e) => setDispExcerptEn(e.target.value)} className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm" data-testid="dispatch-excerpt-en" />
            </label>
            <label className="block lg:col-span-2">
              <span className="text-[12px] text-[color:var(--gv-ink)]/70">URL (vers article/produit)</span>
              <input value={dispUrl} onChange={(e) => setDispUrl(e.target.value)} placeholder="https://geovinum.io/insights/..." className="mt-1 w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-2 text-sm" data-testid="dispatch-url" />
            </label>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={runPreview} disabled={busy} className="btn-secondary disabled:opacity-50" data-testid="dispatch-preview">
              {busy ? <Loader2 className="animate-spin" size={14}/> : <Eye size={14}/>} Preview
            </button>
            <button onClick={runDispatch} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="dispatch-send">
              {busy ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>} Envoyer vers ContentStudio
            </button>
          </div>

          {preview && (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="dispatch-preview-result">
              {["fr", "en"].map((lang) => (
                <div key={lang} className="border border-[color:var(--gv-line)] rounded-[4px] p-3 bg-[color:var(--gv-mist)]/30">
                  <div className="text-[11px] uppercase tracking-wide text-[color:var(--gv-pink)] mb-2 font-semibold">{lang === "fr" ? "Français" : "English"}</div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {preview.image_by_lang?.[lang] && (
                      <div>
                        <div className="text-[10px] text-[color:var(--gv-ink)]/50 mb-1 uppercase">Feed 1:1</div>
                        <img src={preview.image_by_lang[lang]} alt={`feed ${lang}`} className="w-full rounded-[3px] border border-[color:var(--gv-line)]" />
                      </div>
                    )}
                    {preview.story_by_lang?.[lang] && (
                      <div>
                        <div className="text-[10px] text-[color:var(--gv-ink)]/50 mb-1 uppercase">Story 9:16</div>
                        <img src={preview.story_by_lang[lang]} alt={`story ${lang}`} className="w-full rounded-[3px] border border-[color:var(--gv-line)]" />
                      </div>
                    )}
                  </div>
                  <div className="text-[12px] font-semibold mb-1 text-[color:var(--gv-ink)]/70">FB / IG</div>
                  <pre className="text-[12px] whitespace-pre-wrap bg-white border border-[color:var(--gv-line)] rounded-[3px] p-2 mb-3">{preview.captions?.[lang]?.social || ""}</pre>
                  <div className="text-[12px] font-semibold mb-1 text-[color:var(--gv-ink)]/70">LinkedIn</div>
                  <pre className="text-[12px] whitespace-pre-wrap bg-white border border-[color:var(--gv-line)] rounded-[3px] p-2">{preview.captions?.[lang]?.linkedin || ""}</pre>
                </div>
              ))}
            </div>
          )}

          {report && (
            <div className="mt-6 border border-[color:var(--gv-line)] rounded-[4px] p-4 bg-emerald-50" data-testid="dispatch-report">
              <div className="text-sm font-semibold mb-2">Rapport d&apos;envoi</div>
              <ul className="text-[13px] space-y-1">
                {(report.posts || []).map((p, i) => (
                  <li key={i} className={p.ok ? "text-emerald-700" : "text-rose-700"}>
                    {p.ok ? "✓" : "✗"} {p.lang?.toUpperCase()} · {p.kind || ""} {p.id ? `· id=${p.id}` : ""} {p.error ? `· ${p.error}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {savedAt && <div className="text-[12px] text-emerald-700 text-right" data-testid="social-saved-toast">Sauvegardé à {new Date(savedAt).toLocaleTimeString()}</div>}

      {/* Template editor — accordion at the bottom */}
      <TemplateEditorAccordion />
    </div>
  );
};

function TemplateEditorAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <section className="bg-white border border-[color:var(--gv-line)] rounded-[5px]" data-testid="social-templates-accordion">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-[color:var(--gv-mist)]/30 transition"
        data-testid="social-templates-toggle"
      >
        <div>
          <h2 className="font-display text-lg">5. Éditeur visuel des templates</h2>
          <p className="text-xs text-[color:var(--gv-ink)]/60 mt-0.5">3 variantes (Premium · Punchy · Minimal) éditables · preview live</p>
        </div>
        {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {open && (
        <div className="p-5 border-t border-[color:var(--gv-line)]">
          <TemplateEditor />
        </div>
      )}
    </section>
  );
}

export default AdminSocialPage;
